/* The /dxf-repair page.
 *
 * The drawing is the answer, so it goes on screen first and everything else
 * arranges itself around it. What the file says about its own damage sits above
 * the picture, because "this opened here and not in your CAD, and here is why"
 * is the whole reason someone arrived.
 *
 * No worker: the largest real drawing here — 733 KB, 324 blocks — parses in
 * 43 ms. A worker would cost a copy of the file and buy nothing.
 */

import { parse } from './dxf-parse.js'
import { toSvg, toDxf, diagnose } from './dxf-render.js'
import { reportShape } from '../file-shape.js'
import { suggestOtherTool } from '../file-signature.js'

const T = window.__DXF_I18N || {}
const t = (k, fallback, vars) => {
  let s = T[k] || fallback || k
  /* Longest placeholder first: ':n' is a prefix of ':name'. */
  if (vars) {
    for (const [n, v] of Object.entries(vars).sort((a, b) => b[0].length - a[0].length)) {
      s = s.split(':' + n).join(String(v))
    }
  }
  return s
}
const $ = s => document.querySelector(s)
const el = (tag, cls, text) => {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text != null) n.textContent = text
  return n
}

const state = { doc: null, name: '', svg: null, limit: null, hidden: new Set() }

function setStatus(msg, kind = 'info') {
  const box = $('#dxf-status')
  if (!box) return
  box.hidden = !msg
  box.className = 'dxf-status lg-card dxf-status--' + kind
  box.textContent = msg || ''
}

const fmtBytes = n => {
  const u = [t('unit_b', 'B'), t('unit_kb', 'KB'), t('unit_mb', 'MB')]
  let i = 0
  let v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v < 10 && i ? v.toFixed(1) : Math.round(v)} ${u[i]}`
}

async function open(file) {
  state.name = file.name
  setStatus(t('opening', 'Reading the drawing…'))
  $('#dxf-workspace').hidden = true
  $('#dxf-summary').hidden = true

  const text = await file.text()
  const t0 = performance.now()
  const doc = parse(text)
  const ms = Math.round(performance.now() - t0)

  if (!doc.pairCount) {
    /* Not a pair stream at all. Binary DXF and DWG land here, and knowing which
       of the two people actually bring is the difference between writing a
       binary reader and writing nothing. */
    try {
      reportShape({ format: 'dxf', size: file.size, ok: false, reason: 'no-pairs' })
    } catch (_) {}
    setStatus(t('err_not_dxf', 'This file is not a DXF drawing.'), 'error')
    try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'dxf-repair', document.querySelector('#dxf-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
    return
  }
  state.doc = doc
  state.hidden = new Set()
  state.svg = toSvg(doc, { width: 1400 })

  setStatus(state.svg ? '' : t('warn_nothing_to_draw',
    'The file was read, but it holds no geometry to draw — only tables, blocks or settings.'), state.svg ? 'info' : 'warn')

  renderSummary(ms)
  renderLayers()
  renderDrawing()
  /* Which CAD versions arrive and what they contain. `counts` keys are DXF
     entity type names — LINE, SPLINE — never a layer name, which is the one
     string in this format a person authored. */
  try {
    reportShape({
      format: 'dxf',
      version: doc.version,
      size: file.size,
      ok: true,
      flags: [].concat(
        (doc.problems || []).map(p => p && (p.kind || p)),
        state.svg ? [] : ['nothing-to-draw'],
      ),
      counts: Object.assign(
        {
          entities: doc.entities.length,
          layers: doc.layers.length,
          blocks: doc.blocks.size,
          pairs: doc.pairCount,
        },
        doc.counts,
      ),
    })
  } catch (_) { /* telemetry must never break the tool */ }

  try { window.__stage?.('complete') } catch (_) {}
  $('#dxf-workspace').hidden = false
  state.limit?.commit()
  window.__aeToolComplete?.({ entities: doc.entities.length, problems: doc.problems.length })
}

function renderSummary(ms) {
  const box = $('#dxf-summary')
  const d = state.doc
  box.innerHTML = ''
  box.hidden = false

  const chips = el('div', 'dxf-chips')
  const chip = (label, value, kind = '') => {
    if (value == null || value === '') return
    const c = el('span', 'dxf-chip' + (kind ? ' dxf-chip--' + kind : ''))
    c.append(el('b', null, label), document.createTextNode(' '), el('span', null, String(value)))
    chips.append(c)
  }
  chip(t('chip_version', 'Version'), d.version || '—')
  chip(t('chip_entities', 'Objects'), d.entities.length.toLocaleString())
  if (d.layers.length) chip(t('chip_layers', 'Layers'), d.layers.length)
  if (d.blocks.size) chip(t('chip_blocks', 'Blocks'), d.blocks.size)
  if (d.extents) chip(t('chip_size_dwg', 'Extents'), `${Math.round(d.extents.w)} × ${Math.round(d.extents.h)}`)
  const bad = diagnose(d)
  if (bad.length) chip(t('chip_problems', 'Problems'), bad.length, 'warn')
  box.append(chips)

  const notes = el('ul', 'dxf-notes')
  const note = (s, key = false) => notes.append(el('li', key ? 'dxf-note--key' : null, s))

  for (const p of bad) {
    if (p.kind === 'no-eof') {
      note(t('note_no_eof', 'The file has no end marker: it was cut short. CAD programs refuse such a drawing outright — everything up to the cut is drawn below.'), true)
    } else if (p.kind === 'unclosed-section') {
      note(t('note_unclosed_section', 'A section was never closed. The reader carried on to the end of the file instead of stopping there.'), true)
    } else if (p.kind === 'unclosed-block') {
      note(t('note_unclosed_block', 'A block definition was never closed.'), true)
    } else if (p.kind === 'bad-code') {
      note(t('note_bad_code', ':n lines are not a valid group code. They were stepped over rather than treated as the end of the file.', { n: p.n }), true)
    } else if (p.kind === 'stray-endsec') {
      note(t('note_stray_endsec', 'A section end appeared where no section was open.'), true)
    } else if (p.kind === 'orphan-vertex') {
      note(t('note_orphan_vertex', 'Some polyline vertices have no shape to belong to — the polyline they described is gone.'))
    }
  }
  const kinds = Object.entries(d.counts).sort((a, b) => b[1] - a[1])
  if (kinds.length) {
    note(t('note_kinds', 'What is inside: :list', { list: kinds.slice(0, 8).map(([k, n]) => `${k} × ${n}`).join(', ') }))
  }
  note(t('note_read', 'Read in :ms ms — :pairs code/value pairs.', { ms, pairs: d.pairCount.toLocaleString() }))
  box.append(el('h4', 'dxf-sub', t('notes_title', 'What the file says about itself')), notes)
}

function renderLayers() {
  const host = $('#dxf-layers')
  host.innerHTML = ''
  const used = new Set(state.doc.entities.map(e => e.layer))
  const names = [...new Set([...state.doc.layers.map(l => l.name), ...used])].filter(Boolean).sort()
  host.hidden = names.length < 2
  if (host.hidden) return
  for (const name of names) {
    const label = el('label', 'dxf-layer')
    const cb = document.createElement('input')
    cb.type = 'checkbox'
    cb.checked = !state.hidden.has(name)
    cb.addEventListener('change', () => {
      if (cb.checked) state.hidden.delete(name)
      else state.hidden.add(name)
      state.svg = toSvg(state.doc, { width: 1400, layers: visibleLayers() })
      renderDrawing()
    })
    label.append(cb, document.createTextNode(' ' + name))
    host.append(label)
  }
}

const visibleLayers = () => {
  if (!state.hidden.size) return null
  const all = new Set(state.doc.entities.map(e => e.layer))
  for (const h of state.hidden) all.delete(h)
  return all
}

function renderDrawing() {
  const host = $('#dxf-canvas')
  host.innerHTML = ''
  if (!state.svg) {
    host.append(el('p', 'dxf-muted', t('no_preview', 'There is nothing to draw in this file.')))
    return
  }
  const wrap = el('div', 'dxf-svg')
  wrap.innerHTML = state.svg
  host.append(wrap)
}

const stem = () => state.name.replace(/\.dxf$/i, '') || 'drawing'

function exportSvg() {
  if (!state.svg) return
  window.__downloadFile?.(new Blob([state.svg], { type: 'image/svg+xml' }), stem() + '.svg')
}

function exportDxf() {
  if (!state.doc) return
  const text = toDxf(state.doc)
  window.__downloadFile?.(new Blob([text], { type: 'application/dxf' }), stem() + '-repaired.dxf')
}

function wire() {
  const drop = document.getElementById('dxfDrop')
  drop?.addEventListener('tool-uploader:file', async ev => {
    const file = ev.detail?.files?.length ? ev.detail.files[0] : ev.detail?.file
    if (!file) return
    /* Funnel stages. Without them a tool can only report that it finished —
       never that someone arrived, picked a file and got nothing, which is the
       case worth knowing about because that person leaves without writing in. */
    try { window.__stage?.('file_chosen', { mb: Math.round((file.size || 0) / 1048576) }) } catch (_) {}
    state.limit = window.__toolLimit?.begin('dxf-repair', file.name + ':' + file.size) || null
    try {
      await open(file)
    } catch (err) {
      try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'dxf-repair', document.querySelector('#dxf-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
      setStatus(t('err_open', 'This file could not be read: ') + (err?.message || err), 'error')
      window.__handleError?.(err, 'dxf-repair')
    }
  })
  drop?.addEventListener('tool-uploader:clear', () => {
    state.doc = null; state.svg = null
    $('#dxf-workspace').hidden = true
    $('#dxf-summary').hidden = true
    setStatus('')
  })
  $('#dxf-export-svg')?.addEventListener('click', exportSvg)
  $('#dxf-export-dxf')?.addEventListener('click', exportDxf)
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire)
else wire()
